// [MermaidChart: 9a6cb7d0-540e-4c9c-bf76-0cfe8b844582]

import { useState, useEffect, useRef } from 'react';
import { House, Menu, ChevronDown } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { LocaleProvider, useStrings } from './locales';
import { VoidNewProvider } from './hooks/VoidNewContext';
import { ContentVersionProvider } from './hooks/ContentVersionContext';
import { DomainSprite } from './character/cards/DomainSprite';
import Sidebar from './components/Sidebar';
import SearchBar from './components/SearchBar';
import ScrollToTopBtn from './components/ScrollToTopBtn';
import BugReportButton from './components/BugReportButton';
import InfoFab from './components/InfoFab';
import MobileNav from './components/MobileNav';
import MobileSearch from './components/MobileSearch';
import { isSuggestedTheme, themeLabel } from './components/themeHint';
import Home from './pages/Home';
import Introduction from './pages/Introduction';
import CharacterCreation from './pages/CharacterCreation';
import Classes from './pages/Classes';
import Ancestries from './pages/Ancestries';
import Communities from './pages/Communities';
import Domains from './pages/Domains';
import Mechanics from './pages/Mechanics';
import Equipment from './pages/Equipment';
import GMTools from './pages/GMTools';
import Adversaries from './pages/Adversaries';
import Environments from './pages/Environments';
import WitherwildFrame from './pages/WitherwildFrame';
import Items from './pages/Items';
import Beastforms from './pages/Beastforms';
import Transformations from './pages/Transformations';
import About from './pages/About';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Kohd from './pages/Kohd';
//https://lucide.dev/

type Theme = 'dark' | 'light' | 'hc-dark' | 'hc-light' | 'blue';
type FontSize = 'md' | 'lg' | 'xl';

function buildThemes(labels: { dark: string; light: string; hcDark: string; hcLight: string; blue: string }) {
  return [
    { id: 'dark' as Theme, label: labels.dark, swatch: '#3d1f6e', border: '#c9a227' },
    { id: 'light' as Theme, label: labels.light, swatch: '#e5dece', border: '#7a5c10' },
    { id: 'hc-dark' as Theme, label: labels.hcDark, swatch: '#111111', border: '#e0e0e0' },
    { id: 'hc-light' as Theme, label: labels.hcLight, swatch: '#eeeeee', border: '#111111' },
    { id: 'blue' as Theme, label: labels.blue, swatch: '#0b1a30', border: '#4fd8ff' },
  ];
}

// Fallback wrapper for Lucide icons
// class IconBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
// state = { failed: false };
// static getDerivedStateFromError() { return { failed: true }; }
// render() { return this.state.failed ? this.props.fallback : this.props.children; }
// }

interface HeaderProps {
  theme: Theme;
  onSetTheme: (t: Theme) => void;
  fontSize: FontSize;
  onSetFontSize: (s: FontSize) => void;
  onToggleSidebar: () => void; // opens sidebar on mobile; no-op on desktop (hover handles it)
}

function ScrollToTop(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    const el = document.querySelector('.content-area');
    if (el) el.scrollTop = 0;
  }, [pathname]);
  return null;
}

function ScrollToHash(): null {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const scroll = () => {
      const target = document.getElementById(id);
      const container = document.querySelector('.content-area') as HTMLElement | null;
      if (target && container) {
        const offset = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
        container.scrollTop += offset - 24;
      }
    };
    scroll();
    const t = setTimeout(scroll, 120); // retry after page renders
    return () => clearTimeout(t);
  }, [hash, pathname]);
  return null;
}

const FONT_SIZE_IDS: FontSize[] = ['md', 'lg', 'xl'];

function ThemeSelect({ theme, onSetTheme }: { theme: Theme; onSetTheme: (t: Theme) => void }) {
  const s = useStrings();
  const THEMES = buildThemes(s.app.themes);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const current = THEMES.find(t => t.id === theme)!;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  return (
    <div className="theme-select-custom" ref={ref}>
      <button
        className="theme-select-trigger"
        onClick={() => setOpen(o => !o)}
        aria-label={s.app.header.chooseTheme}
        aria-expanded={open}
      >
        <span
          className="theme-select-dot"
          style={{ background: current.swatch, borderColor: current.border }}
        />
        <ChevronDown size={10} className={`theme-select-chevron${open ? ' open' : ''}`} />
      </button>
      {open && (
        <div className="theme-select-menu">
          {THEMES.map(t => (
            <button
              key={t.id}
              className={`theme-select-opt${theme === t.id ? ' active' : ''}${isSuggestedTheme(t.id, theme, pathname) ? ' theme-select-opt--suggested' : ''}`}
              onClick={() => { onSetTheme(t.id); setOpen(false); }}
            >
              <span
                className="theme-select-dot"
                style={{ background: t.swatch, borderColor: t.border }}
              />
              <span className="theme-select-label">{themeLabel(t, theme, pathname)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Header({ theme, onSetTheme, fontSize, onSetFontSize, onToggleSidebar }: HeaderProps) {
  const s = useStrings();
  const { pathname } = useLocation();
  const THEMES = buildThemes(s.app.themes);
  const FONT_SIZES = FONT_SIZE_IDS.map(id => ({
    id,
    label: 'A',
    ariaLabel: s.app.fontSizes[id],
  }));
  return (
    <div className="header">
      <button
        className="hamburger-btn"
        onClick={onToggleSidebar}
        aria-label={s.app.header.toggleNav}
      >
        <Menu size={20} />
      </button>
      <Link to="/" className="mobile-home-btn" aria-label={s.app.header.home}>
        <House size={18} />
      </Link>
      <SearchBar />
      <div className="font-size-btns" role="group" aria-label={s.app.header.textSize}>
        {FONT_SIZES.map((fs) => (
          <button
            key={fs.id}
            type="button"
            className={`font-size-btn${fontSize === fs.id ? ' active' : ''}`}
            onClick={() => onSetFontSize(fs.id)}
            aria-pressed={fontSize === fs.id}
            aria-label={fs.ariaLabel}
            title={fs.ariaLabel}
          >
            {fs.label}
          </button>
        ))}
      </div>
      <div className="theme-swatches" role="group" aria-label={s.app.header.chooseTheme}>
        {THEMES.map(t => (
          <button
            key={t.id}
            type="button"
            className={`theme-swatch${theme === t.id ? ' active' : ''}${isSuggestedTheme(t.id, theme, pathname) ? ' theme-swatch--suggested' : ''}`}
            style={{ background: t.swatch, borderColor: t.border }}
            onClick={() => onSetTheme(t.id)}
            title={themeLabel(t, theme, pathname)}
            aria-pressed={theme === t.id}
            aria-label={themeLabel(t, theme, pathname)}
          />
        ))}
      </div>
      <ThemeSelect theme={theme} onSetTheme={onSetTheme} />
    </div>
  );
}

function AppFooter() {
  const s = useStrings();
  return (
    <footer className="site-footer">
      <div className="site-footer-inner">
        <a
          href="https://darringtonpress.com/license"
          target="_blank"
          rel="noopener noreferrer"
          className="dh-badge-link"
          title="Daggerheart Compatible, Community Gaming License"
        >
          <img className="dh-badge dh-badge--dark" src="/dh-compatible/dh-compatible-white.svg" alt={s.app.footer.badgeAlt} />
          <img className="dh-badge dh-badge--light" src="/dh-compatible/dh-compatible-color.svg" alt={s.app.footer.badgeAlt} />
        </a>
        <a href="https://ko-fi.com/waleedalnaimi" target="_blank" rel="noopener noreferrer" className="kofi-btn">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="15" height="15" style={{ verticalAlign: 'middle', marginRight: '5px' }}>
            <path d="M23.881 8.948c-.773-4.085-4.859-4.593-4.859-4.593H.723c-.604 0-.679.798-.679.798s-.082 7.324-.022 11.822c.164 2.424 2.586 2.672 2.586 2.672s8.267-.023 11.966-.049c2.438-.426 2.683-2.566 2.658-3.734 4.352.24 7.422-2.831 6.649-6.916zm-11.062 3.511c-1.246 1.453-4.011 3.976-4.011 3.976s-.121.119-.31.023c-.076-.057-.108-.09-.108-.09-.443-.441-3.368-3.049-4.034-3.954-.709-.965-1.041-2.7-.091-3.71.951-1.01 3.005-1.086 4.363.407 0 0 1.565-1.782 3.468-.963 1.904.82 1.832 3.011.723 4.311zm6.173.478c-.928.116-1.682.028-1.682.028V7.284h1.77s1.971.551 1.971 2.638c0 1.913-.985 2.667-2.059 3.015z" />
          </svg>
          {s.app.footer.kofi}
        </a>
        <Link to="/about" className="site-footer-about">{s.app.footer.about}</Link>
        <Link to="/privacy" className="site-footer-about">{s.app.footer.privacy}</Link>
      </div>
    </footer>
  );
}

interface AppShellProps {
  theme: Theme;
  onSetTheme: (t: Theme) => void;
  fontSize: FontSize;
  onSetFontSize: (s: FontSize) => void;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onCloseSidebar: () => void;
}

function AppShell({ theme, onSetTheme, fontSize, onSetFontSize, sidebarOpen, onToggleSidebar, onCloseSidebar }: AppShellProps) {
  const s = useStrings();
  return (
    <>
      <a href="#main-content" className="skip-link">{s.app.skipToContent}</a>
      <DomainSprite />
      <ScrollToTop />
      <ScrollToHash />
      <div className="app-layout">
        <Sidebar isOpen={sidebarOpen} onClose={onCloseSidebar} />
        <div className="main-area">
          <Header
            theme={theme}
            onSetTheme={onSetTheme}
            fontSize={fontSize}
            onSetFontSize={onSetFontSize}
            onToggleSidebar={onToggleSidebar}
          />
          <div className="content-area" id="main-content">
            <ScrollToTopBtn />
            <BugReportButton hiddenOnMobile={sidebarOpen} />
            <InfoFab hiddenOnMobile={sidebarOpen} />
            <MobileSearch />
            <MobileNav
              onOpenSidebar={onToggleSidebar}
              theme={theme}
              onSetTheme={onSetTheme}
              fontSize={fontSize}
              onSetFontSize={onSetFontSize}
            />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/introduction" element={<Introduction />} />
              <Route path="/character-creation" element={<CharacterCreation />} />
              <Route path="/classes" element={<Classes />} />
              <Route path="/classes/:className" element={<Classes />} />
              <Route path="/ancestries" element={<Ancestries />} />
              <Route path="/communities" element={<Communities />} />
              <Route path="/transformations" element={<Transformations />} />
              <Route path="/domains" element={<Domains />} />
              <Route path="/mechanics" element={<Mechanics />} />
              <Route path="/equipment" element={<Equipment />} />
              <Route path="/items" element={<Items />} />
              <Route path="/beastforms" element={<Beastforms />} />
              <Route path="/gm-tools" element={<GMTools />} />
              <Route path="/adversaries" element={<Adversaries />} />
              <Route path="/environments" element={<Environments />} />
              <Route path="/campaign-frame" element={<WitherwildFrame />} />
              <Route path="/kohd" element={<Kohd />} />
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="*" element={<Home />} />
            </Routes>
            <AppFooter />
          </div>
        </div>
      </div>
    </>
  );
}

function App() {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem('dhtools-theme') as Theme) || 'dark'
  );
  const [fontSize, setFontSize] = useState<FontSize>(
    () => (localStorage.getItem('dhtools-font') as FontSize) || 'md'
  );
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
    localStorage.setItem('dhtools-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (fontSize === 'md') {
      document.documentElement.removeAttribute('data-font');
    } else {
      document.documentElement.setAttribute('data-font', fontSize);
    }
    localStorage.setItem('dhtools-font', fontSize);
  }, [fontSize]);

  return (
    <Router>
      <VoidNewProvider>
      <ContentVersionProvider>
      <LocaleProvider>
        <AppShell
          theme={theme}
          onSetTheme={setTheme}
          fontSize={fontSize}
          onSetFontSize={setFontSize}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(true)}
          onCloseSidebar={() => setSidebarOpen(false)}
        />
      </LocaleProvider>
      </ContentVersionProvider>
      </VoidNewProvider>
    </Router>
  );
}

export default App;